import type {
  CredentialProvisionRequestType,
  CredentialProvisionResponseStatus,
} from "@clipboard-health/contract-backend-main";

export const CREDENTIAL_ACCOUNT_STATUSES = [
  "active",
  "scheduled",
  "not_scheduled",
  "error",
  "creating",
  "resetting",
] as const;

export type CredentialAccountStatus = typeof CREDENTIAL_ACCOUNT_STATUSES[number];

export function determineCredentialAccountStatus(params: {
  mostRecentRequestStatus?: CredentialProvisionResponseStatus;
  mostRecentRequestType?: string;
  hasExistingProvisionerAccount: boolean;
  hasUpcomingShiftStartingSoon: boolean;
  isFeatureEnabled: boolean;
}): CredentialAccountStatus {
  const {
    mostRecentRequestStatus,
    mostRecentRequestType,
    hasExistingProvisionerAccount,
    hasUpcomingShiftStartingSoon,
    isFeatureEnabled,
  } = params;

  if (mostRecentRequestStatus === "in_progress") {
    if (mostRecentRequestType === "reset_password") {
      return "resetting";
    }
    return "creating";
  }

  if (mostRecentRequestStatus === "failed" || mostRecentRequestStatus === "no_response") {
    return "error";
  }

  if (hasExistingProvisionerAccount) {
    return "active";
  }

  if (isFeatureEnabled && hasUpcomingShiftStartingSoon) {
    return "scheduled";
  }

  return "not_scheduled";
}

export function getActionButtonText(status: CredentialAccountStatus): string {
  // No default as this is exhaustive
  // eslint-disable-next-line default-case
  switch (status) {
    case "active":
    case "error":
    case "resetting": {
      return "Reset";
    }
    case "scheduled":
    case "not_scheduled":
    case "creating": {
      return "Create now";
    }
  }
}

export function getRequestType(status: CredentialAccountStatus): CredentialProvisionRequestType {
  // No default as this is exhaustive
  // eslint-disable-next-line default-case
  switch (status) {
    case "active":
    case "error":
    case "resetting": {
      return "reset_password";
    }
    case "scheduled":
    case "not_scheduled":
    case "creating": {
      return "create_credentials";
    }
  }
}

export function getTooltipText(status: CredentialAccountStatus): string | undefined {
  // No default as this is exhaustive
  // eslint-disable-next-line default-case
  switch (status) {
    case "creating": {
      return "Creating account. This should only take a few minutes.";
    }
    case "resetting": {
      return "Resetting account. This should only take a few minutes.";
    }
    case "error": {
      return "There was an error with the request. Support has been notified and will resolve it as soon as possible.";
    }
    case "active":
    case "scheduled":
    case "not_scheduled": {
      return undefined;
    }
  }
}

export function shouldShowUsername(status: CredentialAccountStatus): boolean {
  return status === "active" || status === "resetting";
}
